// The not-annoying deal (owner 2026-10-17: "nie drażniący gracza"): after two deals of a game in a row that the
// player did not win, the next deal of that game is one that can be won (three in the hard games, assistAfter),
// and so is every deal after it until the player wins one; a win resets the count. Settled with
// the owner: a deal counts as not won when the player made a move in it and then started a new deal of that
// game without winning it (New game, Play in the Games window, a reload); Retry (the same cards again) does
// not count; one count per game; all eight games, with a time limit for the search (engine/solver.ts: the
// first deal it can win, else the easiest it saw: Spider two / four suits and Forty Thieves are hard for it).
// The search runs in a Web Worker as soon as the next deal may need help (one deal not won yet), so the deal
// is ready when the player asks for it; the found pattern waits in localStorage (it survives a reload).
// Off in multiplayer (the server deals) and in the e2e tests unless a test turns it on (the shuffle-parity
// tests compare every deal with a seeded random).
import type { GameId } from '../engine/types';

/** Deals in a row not won before the next one is a helpful one: two (the third can be won); three in the
 *  games the search cannot prove (owner 2026-10-17: so that it does not look like arranged cards there). */
const HARD_GAMES: readonly GameId[] = ['spider:two-suits', 'spider:four-suits', 'forty-thieves'];
export const assistAfter = (game: GameId): number => (HARD_GAMES.includes(game) ? 3 : 2);
/** Games the helper leaves alone: FreeCell (nearly every deal can be won, and the solver knows no free cells). */
const NO_ASSIST: readonly GameId[] = ['freecell', 'pyramid', 'mahjong', 'mahjong-connect', 'hearts', 'makao', 'three-five-eight', 'thousand', 'kulki'];
export const ASSIST_KEY = 'solitaire_storage:assist';
/** How long the worker may look for a winnable deal (it runs while the player plays the deal before): 4 s,
 *  2 s on a phone or tablet (a coarse pointer). A winnable Klondike deal is usually found in 0.05-0.3 s. */
const searchMs = (): number => {
  try {
    return matchMedia('(pointer: coarse)').matches ? 2000 : 4000;
  } catch {
    return 4000;
  }
};

export interface AssistRecord {
  /** Deals of this game in a row the player did not win. */
  failed: number;
  /** The current deal has had a move (abandoning it counts as not won). */
  played: boolean;
  /** A deal found for the next help, and whether the search proved it winnable. */
  pattern?: number[];
  proven?: boolean;
}

type Records = Partial<Record<GameId, AssistRecord>>;

export interface AssistStorage {
  get(): string | null;
  set(value: string): void;
}

export const localAssistStorage: AssistStorage = {
  get: () => {
    try {
      return localStorage.getItem(ASSIST_KEY);
    } catch {
      return null;
    }
  },
  set: (v) => {
    try {
      localStorage.setItem(ASSIST_KEY, v);
    } catch {
      /* no storage: the count lives for this page only */
    }
  },
};

export class Assist {
  private records: Records;
  private searching = new Set<GameId>();

  constructor(
    private readonly storage: AssistStorage,
    /** Starts a search for `game`; it calls `found` when done. */
    private readonly search: (game: GameId) => void,
  ) {
    try {
      this.records = (JSON.parse(storage.get() ?? '{}') ?? {}) as Records;
    } catch {
      this.records = {};
    }
  }

  private rec(game: GameId): AssistRecord {
    return (this.records[game] ??= { failed: 0, played: false });
  }

  private save() {
    this.storage.set(JSON.stringify(this.records));
  }

  private prepare(game: GameId) {
    if (NO_ASSIST.includes(game)) return;
    const r = this.rec(game);
    if (r.failed >= assistAfter(game) - 1 && !r.pattern && !this.searching.has(game)) {
      this.searching.add(game);
      this.search(game);
    }
  }

  /** A new deal of `game` (not a Retry) is about to be made: count the deal it replaces, and give the found
   *  pattern when the player is owed help (else null: a random deal). */
  patternFor(game: GameId): number[] | null {
    const r = this.rec(game);
    if (r.played) {
      r.failed += 1;
      r.played = false;
    }
    let pattern: number[] | null = null;
    if (!NO_ASSIST.includes(game) && r.failed >= assistAfter(game) && r.pattern) {
      pattern = r.pattern;
      delete r.pattern;
      delete r.proven;
    }
    this.save();
    this.prepare(game);
    return pattern;
  }

  /** The player made a move in the current deal of `game`. */
  played(game: GameId) {
    const r = this.rec(game);
    if (r.played) return;
    r.played = true;
    this.save();
    this.prepare(game);
  }

  /** The current deal of `game` was won: the count starts again. */
  won(game: GameId) {
    const r = this.rec(game);
    r.failed = 0;
    r.played = false;
    this.save();
  }

  /** The search for `game` is done. */
  found(game: GameId, pattern: number[], proven: boolean) {
    this.searching.delete(game);
    const r = this.rec(game);
    r.pattern = pattern;
    r.proven = proven;
    this.save();
  }

  record(game: GameId): AssistRecord {
    return { ...this.rec(game) };
  }
}

/** The Web Worker search (assist/solver.worker.ts), or nothing where workers are missing. */
export function workerSearch(
  onFound: (game: GameId, pattern: number[], proven: boolean) => void,
): (game: GameId) => void {
  let worker: Worker | null = null;
  let next = 1;
  return (game) => {
    try {
      worker ??= new Worker(new URL('./solver.worker.ts', import.meta.url), { type: 'module' });
      worker.onmessage = (e: MessageEvent<{ game: GameId; pattern: number[]; proven: boolean }>) =>
        onFound(e.data.game, e.data.pattern, e.data.proven);
      worker.postMessage({ id: next++, game, budgetMs: searchMs() });
    } catch {
      /* no worker: random deals */
    }
  };
}
